/**
 * Phase 4 sync engine: drains the local outbox and pulls master-data
 * changes (docs/offline/PHASE-4-OFFLINE-ARCHITECTURE.md). Transport-agnostic
 * per prompt SS49 - HTTP lives entirely behind the injected `SyncTransport`
 * (apps/web/src/lib/syncTransport.ts today); nothing here imports `fetch`,
 * so the same engine can run unmodified once Android packaging lands.
 *
 * Invariants:
 *  - push is FIFO batches of the outbox, capped at `pushBatchSize`. Per-op
 *    server results: ACKNOWLEDGED/ALREADY_PROCESSED -> DONE; REJECTED or
 *    CONFLICT -> terminal (a poison op must not block the ops behind it -
 *    the queue's FIFO `pending()` selection already lets later, unrelated
 *    ops through once the earlier one leaves PENDING/SENT). An opId absent
 *    from the response (server deliberately left it unresolved) stays SENT
 *    and is retried next cycle.
 *  - pull applies to the replicas, then advances the checkpoint - never the
 *    other way around, so a crash between apply and checkpoint just repeats
 *    an idempotent page.
 *  - single-flight: `running` is an in-memory lock. A crash mid-sync ends
 *    the process, which trivially releases it; nothing about the lock
 *    itself needs to survive a restart because every write it drives is
 *    idempotent on replay (server opId table; client-side plain UPSERTs).
 *  - retry classification comes from the transport (SyncTransportError.kind):
 *    TRANSIENT backs off with jitter and retries; AUTH stops retrying and
 *    surfaces AUTH_REQUIRED; PERMANENT is surfaced as FAILED without ever
 *    discarding a queued operation itself (only a per-op REJECTED does that).
 */
import { SYNC_PROTOCOL_VERSION, SYNC_PUSH_MAX_OPERATIONS, } from '@kortex/shared';
import { SYNC_KEYS } from './state.js';
import { applySyncPullPage } from './pull.js';
import { pruneLocalData } from '../privacy.js';
import { recordServerContact } from '../auth/contact.js';
import { SyncTransportError } from './transport.js';
const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 5 * 60_000;
export class SyncEngine {
    db;
    transport;
    appVersion;
    pushBatchSize;
    running = false;
    networkUp = true;
    authRequired = false;
    lastError = null;
    consecutiveFailures = 0;
    nextAttemptNotBefore = 0;
    lastPruneAt = 0;
    onUsersRevoked;
    constructor(db, transport, options = {}) {
        this.db = db;
        this.transport = transport;
        this.appVersion = options.appVersion ?? 'web';
        this.pushBatchSize = options.pushBatchSize ?? SYNC_PUSH_MAX_OPERATIONS;
        this.onUsersRevoked = options.onUsersRevoked;
    }
    /** The caller (a `navigator.onLine`/online-event listener today) reports
     *  network reachability; the engine never probes this itself so it stays
     *  portable to a bridge with a different connectivity signal. */
    setNetworkStatus(up) {
        this.networkUp = up;
    }
    isRunning() {
        return this.running;
    }
    /**
     * Manual "Sync Now" and the background trigger both call this. The lock
     * makes an overlapping call collapse into a no-op instead of hammering the
     * server twice (prompt: "only one sync process may drain the queue at a
     * time"); `ran: false` tells the caller nothing happened this time
     * (already running, offline, or still inside a backoff window) so it can
     * decide whether to report that back to the user.
     */
    async syncNow() {
        if (this.running)
            return { ran: false };
        if (!this.networkUp)
            return { ran: false };
        if (Date.now() < this.nextAttemptNotBefore)
            return { ran: false };
        this.running = true;
        try {
            // Both phases swallow their own transport errors (into lastError/
            // authRequired/backoff) rather than throwing, so a partial cycle never
            // aborts the other phase - but that also means `syncNow()` itself never
            // throws, and backoff must only be cleared when BOTH phases actually
            // succeeded. Clearing it unconditionally here would erase the very
            // backoff `scheduleBackoff()` just set two lines above.
            const pushOk = await this.drainPush();
            const pullOk = await this.pull();
            if (pushOk && pullOk) {
                await this.pruneOccasionally();
                this.consecutiveFailures = 0;
                this.nextAttemptNotBefore = 0;
                // Cleared HERE, once, only when the whole cycle actually succeeded -
                // not inside drainPush()/pull() themselves. Each phase used to clear
                // these on its OWN success, which meant a failed push got silently
                // erased by the pull that unconditionally runs right after it in the
                // same cycle: the cashier would see the chip go quiet while a bill
                // was still stuck. lastError/authRequired now reflect whatever the
                // most recent phase to run actually did.
                this.lastError = null;
                this.authRequired = false;
            }
            return { ran: true };
        }
        finally {
            this.running = false;
        }
    }
    /**
     * Sends everything still queued, now, and says what is left: for a till
     * about to erase itself (decommissioned, told to wipe, account closing).
     * Push only, no pull; ignores backoff and the network flag - the caller
     * has just heard from the server. `unsent` is work still PENDING or SENT
     * (the only copy is on this till: do not erase); `rejected` is work the
     * server refused, which no retry can deliver.
     */
    async flushPush() {
        if (!this.running) {
            this.running = true;
            try {
                await this.drainPush();
            }
            finally {
                this.running = false;
            }
        }
        const counts = await this.db.syncQueue.counts();
        return { unsent: counts.PENDING + counts.SENT, rejected: counts.REJECTED };
    }
    scheduleBackoff() {
        this.consecutiveFailures += 1;
        const exp = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(this.consecutiveFailures, 8));
        // Full jitter within the top half of the window: never zero (which would
        // defeat backoff entirely) and never the bare exponential (which is what
        // synchronises every idle till into one retry stampede after an outage).
        this.nextAttemptNotBefore = Date.now() + exp * (0.5 + Math.random() * 0.5);
    }
    /**
     * Returns false if anything stopped the drain early (a transport error, or
     * an unexpected local failure such as a full disk) - in every such case
     * the affected batch is left SENT/PENDING for the next cycle rather than
     * throwing out of `syncNow()`, so one bad cycle can never take the whole
     * engine down or leave the single-flight lock in a confusing state for the
     * caller. `handleTransportError` funnels ALL of these into the same
     * lastError/backoff bookkeeping - only a `SyncTransportError` with kind
     * AUTH gets the distinct no-backoff treatment.
     */
    async drainPush() {
        try {
            for (;;) {
                const batch = await this.db.syncQueue.pending(this.pushBatchSize);
                if (batch.length === 0)
                    return true;
                const operations = batch.map(toWireOperation);
                for (const op of batch)
                    await this.db.syncQueue.markSent(op.opId);
                const response = await this.transport.push({
                    deviceId: this.db.context.deviceId,
                    deviceCode: this.db.context.deviceCode,
                    appVersion: this.appVersion,
                    protocolVersion: SYNC_PROTOCOL_VERSION,
                    operations,
                });
                // lastError/authRequired are NOT cleared here on a per-batch success -
                // see the comment in syncNow(): only a fully clean cycle (push AND
                // pull) clears them, so a failure earlier in this same drainPush()
                // loop, or in pull() right after it, is never silently erased by an
                // unrelated later success.
                await this.db.syncState.set(SYNC_KEYS.lastPushAt, new Date().toISOString());
                const byOpId = new Map(response.results.map((r) => [r.opId, r]));
                let resolved = 0;
                for (const op of batch) {
                    const result = byOpId.get(op.opId);
                    if (!result)
                        continue; // left unresolved by the server; stays SENT
                    resolved += 1;
                    switch (result.status) {
                        case 'ACKNOWLEDGED':
                        case 'ALREADY_PROCESSED':
                            await this.db.syncQueue.markDone(op.opId);
                            break;
                        case 'CONFLICT':
                            // Parked for a human, per ADR-008 - never silently overwritten
                            // and never endlessly retried (retrying an opId-reuse conflict
                            // cannot resolve it).
                            await this.db.conflicts.raise({
                                kind: 'SYNC_PUSH_CONFLICT',
                                opId: op.opId,
                                aggregateId: op.aggregateId,
                                serverDetailJson: JSON.stringify({
                                    errorCode: result.errorCode ?? null,
                                    errorMessage: result.errorMessage ?? null,
                                }),
                            });
                            await this.db.syncQueue.markRejected(op.opId, result.errorMessage ?? result.errorCode ?? 'Conflict');
                            break;
                        case 'REJECTED':
                            await this.db.syncQueue.markRejected(op.opId, result.errorMessage ?? result.errorCode ?? 'Rejected');
                            break;
                    }
                }
                if (batch.length < this.pushBatchSize)
                    return true; // nothing left beyond this batch
                if (resolved === 0) {
                    // A full batch came back with every opId unresolved: looping again
                    // immediately would spin forever with no delay. Treat it like a
                    // transient failure so the normal backoff paces the retry instead.
                    this.lastError = 'The server left every queued operation unresolved; will retry.';
                    this.scheduleBackoff();
                    return false;
                }
            }
        }
        catch (error) {
            this.handleTransportError(error);
            return false; // the current batch stays SENT; retried once backoff clears
        }
    }
    /** Returns false if a transport error or unexpected local failure stopped
     *  the pull (cursor left untouched either way, safe to retry next cycle). */
    async pull() {
        try {
            const since = (await this.db.syncState.get(SYNC_KEYS.pullCursor)) ?? undefined;
            const response = await this.transport.pull({
                deviceId: this.db.context.deviceId,
                since,
            });
            // lastError/authRequired are NOT cleared here on success - see the
            // comment in syncNow(): only clearing them once, after a fully clean
            // cycle, avoids a successful pull silently erasing a push failure that
            // just happened moments earlier in the same cycle.
            // `property`/`menuItems` travel as loosely-typed bags on the wire (the
            // server assembles them dynamically); narrowing them here is the same
            // trust boundary apps/web's existing enrolment already applies to its
            // own API reads (posData/index.ts buildSnapshot()).
            const page = {
                property: response.property,
                users: response.users,
                categories: response.categories,
                restaurantTables: response.restaurantTables,
                menuItems: response.menuItems,
                charges: response.charges,
                offers: response.offers,
                entitlement: response.entitlement,
                usersComplete: response.usersComplete,
                localRetentionDays: response.localRetentionDays,
                offlineLockDays: response.offlineLockDays,
            };
            const { revokedUserIds } = await applySyncPullPage(this.db, page);
            if (revokedUserIds.length > 0)
                this.onUsersRevoked?.(revokedUserIds);
            // Advance the checkpoint only now that the apply above has actually
            // run - a crash between the two leaves the OLD cursor in place, so the
            // same (idempotent-to-reapply) page is safely re-pulled, never skipped.
            await this.db.syncState.set(SYNC_KEYS.pullCursor, response.cursor);
            await this.db.syncState.set(SYNC_KEYS.lastPullAt, new Date().toISOString());
            // The server answered: the till lock's clock starts again (auth/contact.ts).
            await recordServerContact(this.db.config);
            return true;
        }
        catch (error) {
            this.handleTransportError(error);
            return false;
        }
    }
    /**
     * Local retention (privacy.ts), at most hourly and only after a clean
     * cycle - so whatever it removes, the server has just confirmed holding.
     * A failure here is logged, never allowed to fail the sync.
     */
    async pruneOccasionally() {
        if (Date.now() - this.lastPruneAt < 60 * 60_000)
            return;
        this.lastPruneAt = Date.now();
        try {
            await pruneLocalData(this.db);
        }
        catch (error) {
            console.warn('[pos-local] local retention prune failed', error);
        }
    }
    handleTransportError(error) {
        if (error instanceof SyncTransportError) {
            this.lastError = error.message;
            if (error.kind === 'AUTH') {
                this.authRequired = true;
                return; // do not schedule a backoff retry storm against a dead session
            }
            this.authRequired = false;
            this.scheduleBackoff();
            return;
        }
        this.authRequired = false;
        this.lastError = error instanceof Error ? error.message : String(error);
        this.scheduleBackoff();
    }
    async status() {
        if (!this.networkUp)
            return 'OFFLINE';
        if (this.running)
            return 'SYNCING';
        if (this.authRequired)
            return 'AUTH_REQUIRED';
        const openConflicts = await this.db.conflicts.listOpen();
        if (openConflicts.length > 0)
            return 'CONFLICT';
        const counts = await this.db.syncQueue.counts();
        // Never report SYNCED while a known failure is sitting in the queue,
        // even if nothing is currently PENDING/SENT (prompt: "never show
        // 'Synced' while known pending failures exist").
        if (counts.REJECTED > 0 || this.lastError)
            return 'FAILED';
        if (counts.PENDING + counts.SENT > 0)
            return 'PENDING';
        return 'SYNCED';
    }
    async diagnostics() {
        const [counts, openConflicts, lastPushAt, lastPullAt, status] = await Promise.all([
            this.db.syncQueue.counts(),
            this.db.conflicts.listOpen(),
            this.db.syncState.get(SYNC_KEYS.lastPushAt),
            this.db.syncState.get(SYNC_KEYS.lastPullAt),
            this.status(),
        ]);
        return {
            deviceId: this.db.context.deviceId,
            deviceCode: this.db.context.deviceCode,
            tenantId: this.db.context.tenantId,
            propertyId: this.db.context.propertyId,
            status,
            networkUp: this.networkUp,
            running: this.running,
            pending: counts.PENDING,
            sent: counts.SENT,
            failed: counts.REJECTED,
            openConflicts: openConflicts.length,
            lastPushAt,
            lastPullAt,
            lastError: this.lastError,
            consecutiveFailures: this.consecutiveFailures,
            protocolVersion: SYNC_PROTOCOL_VERSION,
        };
    }
}
function toWireOperation(op) {
    // The queue stores the canonical ENVELOPE frozen at enqueue (opId, kind,
    // aggregateId, device/tenant/property, payload) - see queue.ts `prepare()`.
    // The wire operation only carries the fields the shared push schema
    // defines; device/tenant/property travel at the request level instead.
    const envelope = JSON.parse(op.payloadJson);
    return {
        opId: op.opId,
        kind: op.kind,
        aggregateId: op.aggregateId,
        contentHash: op.contentHash,
        payload: envelope.payload,
    };
}
//# sourceMappingURL=engine.js.map